import React, { useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { trackBlogPostView } from '../../utils/analytics';

// Blog post content data - imported from modular content files
import blogPostsContent from './content';


const BlogPost = () => {
    const navigate = useNavigate();
    const { slug } = useParams();
    const post = blogPostsContent[slug];

    useEffect(() => {
        if (slug) trackBlogPostView(slug);
    }, [slug]);

    if (!post) {
        return (
            <div style={styles.notFound}>
                <h1>Blog Post Not Found</h1>
                <p>The article you're looking for doesn't exist.</p>
                <button onClick={() => navigate('/blog')} style={styles.backButton}>
                    Back to Blog
                </button>
            </div>
        );
    }

    // Parse markdown-style content to HTML
    const parseContent = (content) => {
        // This is a simplified markdown parser for the blog
        let html = content
            // Headers
            .replace(/^## (.*$)/gm, '<h2 class="blog-h2">$1</h2>')
            .replace(/^### (.*$)/gm, '<h3 class="blog-h3">$1</h3>')
            // Bold
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            // Links
            .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="blog-link" target="_blank" rel="noopener noreferrer">$1</a>')
            // Lists
            .replace(/^- (.*$)/gm, '<li>$1</li>')
            .replace(/^(\d+)\. (.*$)/gm, '<li>$2</li>')
            // Paragraphs
            .replace(/\n\n/g, '</p><p>')
            // Code/backticks
            .replace(/`(.*?)`/g, '<code class="blog-code">$1</code>')
            // Horizontal rule
            .replace(/^---$/gm, '<hr class="blog-hr" />');

        // Wrap lists
        html = html.replace(/(<li>.*<\/li>\n?)+/g, (match) => {
            return '<ul class="blog-ul">' + match + '</ul>';
        });

        // Handle tables
        const tableRegex = /\|(.*)\|\n\|([-|]+)\|\n((\|.*\|\n?)+)/g;
        html = html.replace(tableRegex, (match) => {
            const lines = match.trim().split('\n');
            const headers = lines[0].split('|').filter(h => h.trim());
            const rows = lines.slice(2).map(row => row.split('|').filter(r => r.trim()));

            let tableHtml = '<table class="blog-table"><thead><tr>';
            headers.forEach(h => tableHtml += `<th>${h.trim()}</th>`);
            tableHtml += '</tr></thead><tbody>';
            rows.forEach(row => {
                tableHtml += '<tr>';
                row.forEach(cell => tableHtml += `<td>${cell.trim()}</td>`);
                tableHtml += '</tr>';
            });
            tableHtml += '</tbody></table>';
            return tableHtml;
        });

        return html;
    };

    const articleSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": post.title,
        "description": post.metaDescription,
        "image": "https://www.apiloadtest.com/og-image.jpg",
        "author": {
            "@type": "Organization",
            "name": post.author,
            "url": post.authorUrl
        },
        "publisher": {
            "@type": "Organization",
            "name": "APILoadTest.com",
            "url": "https://www.apiloadtest.com",
            "logo": {
                "@type": "ImageObject",
                "url": "https://www.apiloadtest.com/logo.png"
            }
        },
        "datePublished": post.publishDate,
        "dateModified": post.modifiedDate,
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://www.apiloadtest.com/blog/${slug}`
        },
        "keywords": post.keywords,
        "articleSection": post.category,
        "wordCount": post.content.split(/\s+/).length
    };

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.apiloadtest.com"
            },
            {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://www.apiloadtest.com/blog"
            },
            {
                "@type": "ListItem",
                "position": 3,
                "name": post.title,
                "item": `https://www.apiloadtest.com/blog/${slug}`
            }
        ]
    };

    const faqSchema = post.faq && post.faq.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": post.faq.map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
            }
        }))
    } : null;

    return (
        <div style={styles.page}>
            <Helmet>
                <title>{post.title} | APILoadTest.com Blog</title>
                <meta name="description" content={post.metaDescription} />
                <meta name="keywords" content={post.keywords} />
                <meta name="author" content={post.author} />
                <link rel="canonical" href={`https://www.apiloadtest.com/blog/${slug}`} />

                {/* Open Graph */}
                <meta property="og:title" content={post.title} />
                <meta property="og:description" content={post.metaDescription} />
                <meta property="og:url" content={`https://www.apiloadtest.com/blog/${slug}`} />
                <meta property="og:type" content="article" />
                <meta property="og:image" content="https://www.apiloadtest.com/og-image.jpg" />
                <meta property="article:published_time" content={post.publishDate} />
                <meta property="article:modified_time" content={post.modifiedDate} />
                <meta property="article:section" content={post.category} />
                <meta property="article:author" content={post.author} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={post.title} />
                <meta name="twitter:description" content={post.metaDescription} />
                <meta name="twitter:image" content="https://www.apiloadtest.com/og-image.jpg" />

                {/* AI/Crawler hints */}
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
                <meta name="googlebot" content="index, follow" />
                <meta name="bingbot" content="index, follow" />

                {/* Structured Data for SEO and AI */}
                <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
                {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
            </Helmet>

            <style>{`
                .blog-header-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 24px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .blog-nav {
                    display: flex;
                    gap: 32px;
                }
                
                .blog-try-btn {
                    background: #6366f1;
                    color: white;
                    border: none;
                    padding: 12px 28px;
                    border-radius: 24px;
                    font-size: 15px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                
                .blog-try-btn:hover {
                    background: #4f46e5;
                    transform: translateY(-2px);
                }
                
                .blog-content {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 0 24px;
                }
                
                .blog-content p {
                    font-size: 18px;
                    line-height: 1.8;
                    color: var(--text-primary);
                    margin-bottom: 24px;
                }
                
                .blog-h2 {
                    font-family: 'Agrandir Narrow', 'Agrandir', Arial, sans-serif;
                    font-size: 32px;
                    font-weight: 700;
                    color: var(--text-primary);
                    margin: 48px 0 24px;
                    line-height: 1.3;
                }
                
                .blog-h3 {
                    font-family: 'Agrandir Narrow', 'Agrandir', Arial, sans-serif;
                    font-size: 24px;
                    font-weight: 600;
                    color: var(--text-primary);
                    margin: 36px 0 16px;
                }
                
                .blog-link {
                    color: #6366f1;
                    text-decoration: none;
                    border-bottom: 2px solid rgba(99, 102, 241, 0.3);
                    transition: border-color 0.2s ease;
                }
                
                .blog-link:hover {
                    border-bottom-color: #6366f1;
                }
                
                .blog-ul {
                    margin: 20px 0;
                    padding-left: 24px;
                }
                
                .blog-ul li {
                    font-size: 18px;
                    line-height: 1.8;
                    color: var(--text-primary);
                    margin-bottom: 12px;
                }
                
                .blog-code {
                    background: rgba(99, 102, 241, 0.1);
                    color: #6366f1;
                    padding: 2px 8px;
                    border-radius: 4px;
                    font-family: 'SF Mono', 'Consolas', monospace;
                    font-size: 0.9em;
                }
                
                .blog-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 24px 0;
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
                }
                
                .blog-table th {
                    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
                    color: white;
                    padding: 14px 16px;
                    text-align: left;
                    font-weight: 600;
                }
                
                .blog-table td {
                    padding: 14px 16px;
                    border-bottom: 1px solid var(--border-color);
                    background: white;
                }
                
                .blog-table tr:last-child td {
                    border-bottom: none;
                }
                
                .blog-hr {
                    border: none;
                    height: 1px;
                    background: linear-gradient(90deg, transparent, var(--border-color), transparent);
                    margin: 48px 0;
                }
                
                .share-section {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    margin-top: 48px;
                    padding-top: 32px;
                    border-top: 1px solid var(--border-color);
                }
                
                .share-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    border-radius: 20px;
                    border: 1px solid var(--border-color);
                    background: white;
                    color: var(--text-primary);
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-decoration: none;
                }
                
                .share-btn:hover {
                    background: var(--light-gray);
                    border-color: #6366f1;
                }
                
                @media (max-width: 768px) {
                    .blog-nav {
                        display: none;
                    }
                    
                    .blog-h2 {
                        font-size: 26px;
                    }
                    
                    .blog-content p {
                        font-size: 16px;
                    }
                }
            `}</style>

            {/* Header */}
            <header style={styles.header}>
                <div className="blog-header-container">
                    <Link to="/" style={styles.logoSection}>
                        <img src="/logo.png" alt="APILoadTest.com" style={styles.logoImage} />
                        <span style={styles.logoText}>APILoadTest.com</span>
                    </Link>

                    <nav className="blog-nav">
                        <Link to="/#features" style={styles.navLink}>Features</Link>
                        <Link to="/#pricing" style={styles.navLink}>Pricing</Link>
                        <Link to="/blog" style={{ ...styles.navLink, color: '#6366f1' }}>Blog</Link>
                    </nav>

                    <button
                        className="blog-try-btn"
                        onClick={() => navigate('/login')}
                    >
                        Get Started
                    </button>
                </div>
            </header>

            {/* Breadcrumb */}
            <nav style={styles.breadcrumb} aria-label="Breadcrumb">
                <Link to="/" style={styles.breadcrumbLink}>Home</Link>
                <span style={styles.breadcrumbSeparator}>/</span>
                <Link to="/blog" style={styles.breadcrumbLink}>Blog</Link>
                <span style={styles.breadcrumbSeparator}>/</span>
                <span style={styles.breadcrumbCurrent}>{post.title}</span>
            </nav>

            {/* Article */}
            <article style={styles.articleContainer} itemScope itemType="https://schema.org/Article">
                <header style={styles.articleHeader}>
                    <span style={styles.category}>{post.category}</span>
                    <h1 style={styles.articleTitle} itemProp="headline">{post.title}</h1>
                    <div style={styles.articleMeta}>
                        <span itemProp="author" itemScope itemType="https://schema.org/Organization">
                            <span itemProp="name">{post.author}</span>
                        </span>
                        <span>•</span>
                        <time dateTime={post.publishDate} itemProp="datePublished">
                            {new Date(post.publishDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </time>
                        <span>•</span>
                        <span>{post.readTime}</span>
                    </div>
                </header>

                <div
                    className="blog-content"
                    itemProp="articleBody"
                    dangerouslySetInnerHTML={{ __html: `<p>${parseContent(post.content)}</p>` }}
                />

                {/* Share Section */}
                <div className="blog-content">
                    <div className="share-section">
                        <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>Share:</span>
                        <a
                            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(`https://www.apiloadtest.com/blog/${slug}`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="share-btn"
                        >
                            𝕏 Twitter
                        </a>
                        <a
                            href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(`https://www.apiloadtest.com/blog/${slug}`)}&title=${encodeURIComponent(post.title)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="share-btn"
                        >
                            💼 LinkedIn
                        </a>
                    </div>
                </div>
            </article>

            {/* Related CTA */}
            <section style={styles.ctaSection}>
                <div style={styles.ctaContent}>
                    <h2 style={styles.ctaTitle}>Ready to Test Your APIs?</h2>
                    <p style={styles.ctaDescription}>
                        Put load testing into practice.
                    </p>
                    <button
                        style={styles.ctaButton}
                        onClick={() => navigate('/login')}
                    >
                        Get Started
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer style={styles.footer}>
                <div style={styles.footerContent}>
                    <div style={styles.footerLogo}>
                        <img src="/logo.png" alt="APILoadTest.com" style={styles.footerLogoImage} />
                        <span style={styles.footerLogoText}>APILoadTest.com</span>
                    </div>
                    <div style={styles.footerLinks}>
                        <a href="/terms" style={styles.footerLink}>Terms of Service</a>
                        <a href="/privacy" style={styles.footerLink}>Privacy Policy</a>
                        <a href="/refund" style={styles.footerLink}>Refund Policy</a>
                        <a href="mailto:admin@apiloadtest.com" style={styles.footerLink}>Contact</a>
                    </div>
                    <p style={styles.footerEmail}>Support: <a href="mailto:admin@apiloadtest.com" style={styles.footerEmailLink}>admin@apiloadtest.com</a></p>
                    <p style={styles.copyright}>© 2026 APILoadTest.com. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

const styles = {
    page: {
        minHeight: '100vh',
        background: 'var(--light-gray)',
    },
    header: {
        background: 'var(--primary-white)',
        padding: '16px 0',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: '1px solid var(--border-color)',
    },
    logoSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        textDecoration: 'none',
    },
    logoImage: {
        height: '40px',
        width: 'auto',
    },
    logoText: {
        fontFamily: "'Agrandir Narrow', 'Agrandir', Arial, sans-serif",
        fontSize: '24px',
        fontWeight: '700',
        color: 'var(--text-primary)',
    },
    navLink: {
        color: 'var(--text-primary)',
        textDecoration: 'none',
        fontSize: '15px',
        fontWeight: '500',
        transition: 'color 0.2s ease',
    },
    breadcrumb: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '24px',
        fontSize: '14px',
    },
    breadcrumbLink: {
        color: '#6366f1',
        textDecoration: 'none',
    },
    breadcrumbSeparator: {
        margin: '0 8px',
        color: 'var(--text-secondary)',
    },
    breadcrumbCurrent: {
        color: 'var(--text-secondary)',
    },
    articleContainer: {
        paddingBottom: '60px',
    },
    articleHeader: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '0 24px 48px',
        textAlign: 'center',
    },
    category: {
        display: 'inline-block',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        padding: '6px 16px',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '600',
        marginBottom: '24px',
    },
    articleTitle: {
        fontFamily: "'Agrandir Narrow', 'Agrandir', Arial, sans-serif",
        fontSize: '48px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        marginBottom: '24px',
        lineHeight: '1.2',
    },
    articleMeta: {
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        color: 'var(--text-secondary)',
        fontSize: '15px',
    },
    ctaSection: {
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        padding: '80px 24px',
        textAlign: 'center',
    },
    ctaContent: {
        maxWidth: '600px',
        margin: '0 auto',
    },
    ctaTitle: {
        fontSize: '36px',
        fontWeight: '700',
        color: 'white',
        marginBottom: '16px',
    },
    ctaDescription: {
        fontSize: '18px',
        color: 'rgba(255, 255, 255, 0.9)',
        marginBottom: '32px',
    },
    ctaButton: {
        background: 'white',
        color: '#6366f1',
        border: 'none',
        padding: '16px 40px',
        borderRadius: '30px',
        fontSize: '16px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
    },
    footer: {
        background: 'var(--primary-white)',
        padding: '40px 24px',
        borderTop: '1px solid var(--border-color)',
    },
    footerContent: {
        maxWidth: '1200px',
        margin: '0 auto',
        textAlign: 'center',
    },
    footerLogo: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        marginBottom: '16px',
    },
    footerLogoImage: {
        height: '32px',
        width: 'auto',
    },
    footerLogoText: {
        fontFamily: "'Agrandir Narrow', 'Agrandir', Arial, sans-serif",
        fontSize: '20px',
        fontWeight: '700',
        color: 'var(--text-primary)',
    },
    copyright: {
        fontSize: '14px',
        color: 'var(--text-secondary)',
    },
    footerLinks: {
        display: 'flex',
        gap: '24px',
        justifyContent: 'center',
        marginBottom: '16px',
    },
    footerLink: {
        color: 'var(--text-secondary)',
        textDecoration: 'none',
        fontSize: '14px',
        transition: 'color 0.2s ease',
    },
    footerEmail: {
        fontSize: '14px',
        color: 'var(--text-secondary)',
        marginBottom: '16px',
    },
    footerEmailLink: {
        color: '#6366f1',
        textDecoration: 'none',
        fontWeight: '600',
    },
    notFound: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '24px',
    },
    backButton: {
        marginTop: '24px',
        background: '#6366f1',
        color: 'white',
        border: 'none',
        padding: '12px 32px',
        borderRadius: '24px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
    },
};

export default BlogPost;
